import React from 'react';

import { useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectInColorBlindMode,
  selectShortestAvailableBin,
  selectTargetDate,
} from 'state/workflowSlice';

import { formatTimeOfDay12HrClock } from 'features/common/datetimeUtils';
import { formatDistance, formatSpeedKmph } from 'features/common/utils';
import {
  colorForQueueState,
  get_total_info,
} from 'features/workflow_queuing/QueueChartTooltipContents';
import {
  RouteQueueTagma,
  colorRampForColorStatus,
  get_congestion,
  get_minute_offset,
  get_queue_id,
} from 'features/workflow_queuing/common';
import { NA_String } from 'features/workflow_timeline/timelineTooltipContents';

import style from 'features/workflow_queuing/WorkflowQueuing.module.css';

export type TooltipData = {
  tagma: RouteQueueTagma;
};

export function colorForSegmentSpeed(
  tagma: RouteQueueTagma,
  colorBlindMode: boolean,
) {
  const colormap = colorRampForColorStatus(colorBlindMode, true);
  const congestion = get_congestion(tagma as any);
  if (congestion !== null) {
    return colormap.sample(congestion).toString();
  }
  return undefined;
}

export function QueueMapTooltipContents({
  tooltipData,
  queues,
}: {
  tooltipData: TooltipData;
  queues: { [key: number]: RouteQueueTagma };
}) {
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const targetDate = useAppSelector(selectTargetDate);
  const binSize = useAppSelector(selectShortestAvailableBin);
  const colorBlindMode = useAppSelector(selectInColorBlindMode);

  const get_total_queue_info = (queue_id) => {
    const queue: any = queues[queue_id] || [];
    return get_total_info(queue);
  };

  const speed = (tooltipData.tagma.properties as any).average_speed_kmph;
  const formattedSpeed = speed
    ? formatSpeedKmph(speed, userProject.uses_metric)
    : NA_String;

  const total_queue_info = get_total_queue_info(
    get_queue_id(tooltipData.tagma),
  );

  const segmentInfoChunk = (
    <span
      className={style.map_tooltip_big_metric}
      style={{
        color: colorForSegmentSpeed(
          tooltipData.tagma,
          colorBlindMode as boolean,
        ),
      }}
    >
      {formattedSpeed}
    </span>
  );

  const queueInfoBlock = get_queue_id(tooltipData.tagma) ? (
    <span
      className={style.map_tooltip_big_metric}
      style={{ color: colorForQueueState(tooltipData.tagma) }}
    >
      {formatDistance(
        total_queue_info.total_length_km * 1000,
        userProject.uses_metric,
      )}
    </span>
  ) : null;

  const hasQueue = !!queueInfoBlock;
  let titleRow = (
    <div className={style.chart_tooltip_title_row}>
      <h1>{hasQueue ? 'Queue length' : 'Segment speed'}</h1>
    </div>
  );
  if (!hasQueue && formattedSpeed === NA_String) {
    titleRow = null;
  }
  return (
    <div className={style.map_tooltip_container}>
      {titleRow}
      {hasQueue ? queueInfoBlock : segmentInfoChunk}
      <div className={style.chart_tooltip_title_row}>
        {hasQueue && `${formattedSpeed} - `}
        {formatTimeOfDay12HrClock(
          get_minute_offset(tooltipData.tagma, targetDate),
        )}
        {' to '}
        {formatTimeOfDay12HrClock(
          get_minute_offset(tooltipData.tagma, targetDate) + binSize,
        )}
      </div>
    </div>
  );
}
